<script setup lang="ts">
import { type FetchTicketResponse } from "src/api/models";
import type { Column } from "src/types/trello-board-type";
import draggable from "vuedraggable";
import TrelloBoardItem from "./TrelloBoardItem.vue";
import Tag from "primevue/tag";
import { formatDate } from "date-fns";
import SharedLoadingIndicator from "../shared/SharedLoadingIndicator.vue";
import TrelloWorkloadBreakdownPanel from "./TrelloWorkloadBreakdownPanel.vue";
import { useTicketService } from "src/services/ticket-service";
import { combineDateWithTime, toLocalDateString } from "src/utils/date-utc";
import { useNotification } from "src/composables/useNotification";
import { computed, nextTick } from "vue";
import { useTrelloBoardStore } from "src/stores/trello-board-store";

const props = defineProps<{
  column: Column<FetchTicketResponse>;
  selectedStatusSort: string[];
  ticketContainerWidth: number;
  loading?: boolean;
  singleLaneScroll?: boolean;
  hideWorkload?: boolean;
  hideDate?: boolean;
  draggingTicket?: boolean;
  showWorkloadLegend?: boolean;
  showWorkloadBreakdown?: boolean;
}>();

const emits = defineEmits<{
  "update:column": [value: Column<FetchTicketResponse>];
  "context-menu": [event: MouseEvent, ticket: FetchTicketResponse | null, column: Column<FetchTicketResponse>];
  "click-item": [ticket: FetchTicketResponse];
  "workload-update": [];
  "dragging-start": [ticket: FetchTicketResponse];
  "workload-bar-click": [column: Column<FetchTicketResponse>];
}>();

const { updateTicketPlannedDate } = useTicketService();
const { showError } = useNotification();
const trelloBoardStore = useTrelloBoardStore();

const ATTENTION_FIELDS: (keyof FetchTicketResponse)[] = [
  'assignee',
  'site',
  'organisation',
  'expected_duration',
  'unit',
];

function isTicketIncomplete(ticket: FetchTicketResponse): boolean {
  return ATTENTION_FIELDS.some((key) => ticket[key] == null);
}

const attentionCount = computed(() =>
  props.column.data.filter(isTicketIncomplete).length,
);

const firstAttentionTicketId = computed(() => {
  const t = props.column.data.find(isTicketIncomplete);
  return t?.id ?? null;
});

const firstReadyTicketId = computed(() => {
  const t = props.column.data.find((t) => !isTicketIncomplete(t));
  return t?.id ?? null;
});

const ticketStatuses = computed(() =>
  trelloBoardStore.ticketStatuses.filter((s) => s.visible_on_board !== false)
);

const ticketCountByStatus = computed(() => {
  const counts = new Map<string | undefined, number>();
  for (const status of ticketStatuses.value) {
    counts.set(status?.id, props.column.data.filter((t) => t.status?.id === status?.id).length);
  }
  return counts;
});

// Track the first ticket ID per status group for rendering inline headers
const firstTicketIdPerStatus = computed(() => {
  const firsts = new Map<string | undefined, string>();
  for (const ticket of props.column.data) {
    const statusId = ticket.status?.id;
    if (!firsts.has(statusId)) {
      firsts.set(statusId, ticket.id);
    }
  }
  return firsts;
});

function isStatusVisible(ticket: FetchTicketResponse): boolean {
  return props.selectedStatusSort.length === 0
    || props.selectedStatusSort.some((s) => s && s === ticket.status?.name);
}

const HOURS_PER_MECHANIC = 8;

const barColor = computed(() => getWorkloadSeverity(props.column.workload));

// Assigned segment: capped at 100% for visual display (BAR-01, BAR-02)
const assignedBarWidth = computed(() => {
  if (props.column.workload === null) return 0;
  return Math.min(props.column.workload, 100);
});

// Unassigned segment width as a percent of total capacity (BAR-03)
// Total capacity = mechanic count × 8h; fall back to 8h if no mechanics.
// Clamped so assigned + unassigned never exceed 100% visually.
const unassignedBarWidth = computed(() => {
  if (props.column.unassignedHours <= 0) return 0;
  const mechanicCount = props.column.mechanicBreakdown.length;
  const capacityHours = mechanicCount > 0 ? mechanicCount * HOURS_PER_MECHANIC : HOURS_PER_MECHANIC;
  const raw = (props.column.unassignedHours / capacityHours) * 100;
  return Math.min(raw, 100 - assignedBarWidth.value);
});

// Overflow percent to show below the bar when capacity is exceeded (BAR-02)
const overflowPercent = computed(() => {
  if (props.column.workloadOverflow === null || props.column.workloadOverflow <= 0) return null;
  return Math.round(props.column.workloadOverflow);
});

// Tooltip string for mechanic breakdown (BAR-04)
const mechanicTooltip = computed(() => {
  if (!props.column.mechanicBreakdown.length) return null;
  const totalAvailable = props.column.mechanicBreakdown.length * HOURS_PER_MECHANIC;
  const totalUsed = props.column.mechanicBreakdown.reduce((sum, m) => sum + m.scheduledHours, 0);
  const rawPercent = Math.round((totalUsed / totalAvailable) * 100);

  const lines: string[] = [];
  lines.push(`Total: ${totalUsed.toFixed(1)}h / ${totalAvailable.toFixed(1)}h (${rawPercent}%)`);
  if (props.column.unassignedHours > 0) {
    lines.push(`Unassigned: ${props.column.unassignedHours.toFixed(1)}h`);
  }
  lines.push("───");
  for (const m of props.column.mechanicBreakdown) {
    const name = m.name ?? m.id;
    lines.push(`${name}: ${m.scheduledHours.toFixed(1)}h (${Math.round(m.percentage)}%)`);
  }
  return lines.join("\n");
});

const workloadLegendHtml = [
  '<div style="text-align:left;line-height:1.6">',
  '<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--p-green-200);vertical-align:middle"></span> &lt; 80% capacity',
  '<br><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--p-yellow-200);vertical-align:middle"></span> 80–99% capacity',
  '<br><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--p-red-200);vertical-align:middle"></span> ≥ 100% capacity',
  '<br><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:repeating-linear-gradient(45deg,var(--p-amber-300) 0px,var(--p-amber-300) 2px,var(--p-amber-100) 2px,var(--p-amber-100) 4px);vertical-align:middle"></span> Unassigned hours',
  '</div>',
].join('');

function getWorkloadSeverity(workload: number | null): string {
  if (workload === null || isNaN(workload)) return "";
  if (workload < 80) return "bg-green-200 dark:bg-green-800/50";
  if (workload < 100) return "bg-yellow-200 dark:bg-yellow-800/50";
  return "bg-red-200 dark:bg-red-800/50";
}

function onDelete(ticketId: string) {
  const updatedColumn = {
    ...props.column,
    data: props.column.data.filter((t) => t.id !== ticketId),
  };
  emits("update:column", updatedColumn);
}

function onMove(evt: any) {
  const toColumn = evt.relatedContext.component.$parent.$props.column;
  if (toColumn.id === "unresolved") return false;

  const ticket = evt.draggedContext.element;
  if (ticket?.id && ticket.pinned) {
    return false;
  }

  return true;
}

// Multiple <draggable> instances per column share the same column.data array.
// When we mutate that array, Vue reactivity triggers phantom @start/@end events
// on sibling draggables. These guards prevent those phantoms from firing.
let draggedTicket: FetchTicketResponse | null = null;
let draggedFromIndex = -1;
let isProcessingDrag = false;

function onDragStart(evt: any) {
  if (isProcessingDrag) return;
  const sourceList = evt.from.__draggable_component__?.list;
  if (sourceList) {
    draggedTicket = sourceList[evt.oldDraggableIndex] ?? null;
    draggedFromIndex = evt.oldDraggableIndex ?? -1;
  }
}

function removeTicketFromList(list: FetchTicketResponse[], ticketId: string) {
  const idx = list.findIndex((t) => t.id === ticketId);
  if (idx !== -1) list.splice(idx, 1);
}

async function onDragEnd(evt: any) {
  if (isProcessingDrag) return;

  const ticket = draggedTicket;
  draggedTicket = null;
  if (!ticket?.id) return;

  const fromList: FetchTicketResponse[] = evt.from.__draggable_component__?.list;
  const toList: FetchTicketResponse[] = evt.to.__draggable_component__?.list;
  const fromColumnDate = evt.from.__draggable_component__?.$parent?.$props?.column?.date;
  const toColumnDate = evt.to.__draggable_component__?.$parent?.$props?.column?.date;

  const fromDateStr = fromColumnDate ? toLocalDateString(fromColumnDate) : undefined;
  const toDateStr = toColumnDate ? toLocalDateString(toColumnDate) : undefined;
  const sameColumn = fromDateStr && toDateStr ? fromDateStr === toDateStr : evt.from === evt.to;

  if (sameColumn && evt.oldDraggableIndex === evt.newDraggableIndex) return;

  if (ticket.pinned) {
    if (toList) removeTicketFromList(toList, ticket.id);
    if (fromList && !fromList.some((t) => t.id === ticket.id)) {
      fromList.push(ticket);
    }
    return;
  }

  const originalIndex = draggedFromIndex;
  draggedFromIndex = -1;

  // Mutate arrays inside the guard to block phantom events
  isProcessingDrag = true;
  if (fromList && fromList !== toList) {
    removeTicketFromList(fromList, ticket.id);
  }
  if (toList) {
    removeTicketFromList(toList, ticket.id);
    if (isTicketIncomplete(ticket)) {
      const firstReadyIdx = toList.findIndex((t) => !isTicketIncomplete(t));
      if (firstReadyIdx === -1) {
        toList.push(ticket);
      } else {
        toList.splice(firstReadyIdx, 0, ticket);
      }
    } else {
      toList.push(ticket);
    }
  }
  await nextTick();
  isProcessingDrag = false;

  const originalScheduledDate = ticket.scheduled_date;

  try {
    if (!toColumnDate) {
      ticket.scheduled_date = null;
      await updateTicketPlannedDate(ticket.id, null as unknown as string);
      return;
    }

    const newDateStr = toLocalDateString(toColumnDate);
    const newScheduledDate = combineDateWithTime(newDateStr, ticket.scheduled_date);
    ticket.scheduled_date = newScheduledDate;
    await updateTicketPlannedDate(ticket.id, newScheduledDate);
  } catch (error) {
    console.error("Failed to update ticket scheduled_date:", error);
    ticket.scheduled_date = originalScheduledDate;
    if (toList) removeTicketFromList(toList, ticket.id);
    if (fromList && !fromList.some((t) => t.id === ticket.id)) {
      if (originalIndex >= 0 && originalIndex < fromList.length) {
        fromList.splice(originalIndex, 0, ticket);
      } else {
        fromList.push(ticket);
      }
    }
    showError({ message: "Failed to reschedule ticket" });
  }
}

function handleChangeDragging() {
  emits("workload-update");
}
</script>

<template>
  <div
    :style="`min-width:${ticketContainerWidth}px; max-width:${ticketContainerWidth}px`"
    class="flex flex-col"
    :class="{ 'h-full': singleLaneScroll }"
    @contextmenu.prevent="$emit('context-menu', $event, null, column)"
  >
    <!-- Lane header -->
    <div class="sticky top-0 bg-surface-card/95 backdrop-blur-sm z-10" :class="{ 'flex-none': singleLaneScroll }">
      <div class="p-3 border-bottom-1 border-surface-200 dark:border-surface-700">
        <div v-if="hideWorkload !== true" class="flex items-center gap-1 mb-1.5 relative">
        <div
          data-testid="workload-bar"
          class="h-5 bg-surface-200 dark:bg-surface-700 rounded-md relative overflow-hidden flex-1 transition-all duration-200"
          :class="column.mechanicBreakdown.length > 0 ? 'cursor-pointer hover:brightness-110' : 'cursor-default'"
          v-tooltip.top="mechanicTooltip ?? undefined"
          @click="column.mechanicBreakdown.length > 0 && emits('workload-bar-click', column)"
        >
          <!-- Assigned capacity segment (green/yellow/red by threshold) -->
          <div
            :class="`h-full transition-all duration-300 ${barColor}`"
            :style="`width: ${assignedBarWidth}%`"
          />
          <!-- Unassigned hours segment: striped amber, positioned right after assigned segment -->
          <div
            v-if="unassignedBarWidth > 0"
            class="h-full transition-all duration-300 absolute top-0"
            :style="`left: ${assignedBarWidth}%; width: ${unassignedBarWidth}%; background: repeating-linear-gradient(45deg, var(--p-amber-500) 0px, var(--p-amber-500) 3px, var(--p-amber-200) 3px, var(--p-amber-200) 6px);`"
          />
          <!-- Percentage label: centered on bar when wide enough, pushed right when bar is a sliver -->
          <span
            v-if="column.workload !== null"
            data-testid="workload-percentage"
            class="absolute inset-0 flex items-center text-xs font-semibold leading-none pointer-events-none"
            :class="assignedBarWidth < 15
              ? 'text-surface-600 dark:text-surface-300'
              : 'justify-center text-surface-700 dark:text-surface-200'"
            :style="assignedBarWidth < 15 ? `padding-left: calc(${assignedBarWidth}% + 4px)` : undefined"
          >
            {{ Math.round(column.workload) }}%
          </span>
        </div>
        <i
          v-if="showWorkloadLegend"
          class="pi pi-info-circle text-xs text-400 cursor-help flex-none"
          v-tooltip.top="{ value: workloadLegendHtml, escape: false }"
        />
        </div>
        <TrelloWorkloadBreakdownPanel
          v-if="showWorkloadBreakdown"
          :mechanic-breakdown="column.mechanicBreakdown"
          :unassigned-hours="column.unassignedHours"
          :workload="column.workload"
          :column-title="column.title"
          @close="emits('workload-bar-click', column)"
        />
        <!-- Overflow label: rendered below the bar when workload exceeds 100% (BAR-02) -->
        <div
          v-if="overflowPercent !== null"
          class="text-xs text-right font-semibold text-red-500 leading-none mt-0.5 mb-1.5"
        >
          +{{ overflowPercent }}% overloaded
        </div>
        <div class="flex flex-col items-center">
          <span v-if="hideDate !== true" class="text-sm text-600">{{ formatDate(column.date, "d MMM") }}</span>
          <div class="flex items-center gap-2 mt-1">
            <span class="text-lg font-medium" :class="{ 'text-primary': column.highlight }">
              {{ column.title }}
            </span>
            <Tag :value="column.data.length" severity="info" class="px-2 py-1" />
            <Tag
              v-if="attentionCount > 0"
              :value="`${attentionCount}`"
              severity="warn"
              class="px-2 py-1"
              v-tooltip.top="`${attentionCount} ticket${attentionCount !== 1 ? 's' : ''} missing required fields`"
            >
              <template #default>
                <i class="pi pi-exclamation-triangle mr-1" style="font-size: 0.65rem"></i>{{ attentionCount }}
              </template>
            </Tag>
            <Tag
              v-if="column.missingDurationCount > 0"
              :value="`${column.missingDurationCount} missing`"
              severity="warn"
              class="px-2 py-1"
              v-tooltip.top="`${column.missingDurationCount} ticket${column.missingDurationCount !== 1 ? 's' : ''} missing expected duration`"
            />
            <slot name="header-actions"></slot>
          </div>
        </div>
      </div>
    </div>

    <div data-testid="column-scroll-area" class="p-3 flex-1 content-scroll bg-surface-50/70 dark:bg-surface-900/80 border-1 border-surface-200 dark:border-surface-700 border-round-xl transition-colors" :class="{ 'overflow-y-auto': singleLaneScroll, 'bg-primary/5 dark:bg-primary/10': column.highlight, 'ring-2 ring-primary/20 ring-inset bg-primary/5 dark:bg-primary/10': draggingTicket && column.id !== 'unresolved', 'ring-2 ring-red-300/30 ring-inset': draggingTicket && column.id === 'unresolved' }" v-if="!loading">
      <draggable
        :list="column.data"
        :group="{ name: 'tickets' }"
        item-key="id"
        :animation="200"
        :move="onMove"
        ghost-class="drag-ghost"
        chosen-class="drag-chosen"

        class="space-y-2 min-h-full"
        @start="(e: any) => { onDragStart(e); $emit('dragging-start', e); }"
        @end="onDragEnd"
        @change="handleChangeDragging"
      >
        <template #item="{ element: ticket }">
          <div v-show="isStatusVisible(ticket)">
            <!-- Attention group header — rendered above the first incomplete ticket -->
            <div
              v-if="attentionCount > 0 && firstAttentionTicketId === ticket.id"
              data-testid="attention-header"
              class="flex items-center gap-2 mb-2 mt-1 first:mt-0"
            >
              <i class="pi pi-exclamation-triangle text-orange-500 dark:text-orange-400" style="font-size: 0.75rem"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">Requires attention</span>
              <span data-testid="attention-count" class="text-xs font-medium text-orange-500 dark:text-orange-400">{{ attentionCount }}</span>
            </div>

            <!-- Ready group header — rendered above the first complete ticket when there are also incomplete tickets -->
            <div
              v-if="attentionCount > 0 && firstReadyTicketId === ticket.id"
              data-testid="ready-header"
              class="flex items-center gap-2 mb-2 mt-3 pt-2 border-t border-surface-200 dark:border-surface-700"
            >
              <i class="pi pi-check-circle text-green-500 dark:text-green-400" style="font-size: 0.75rem"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-500">Ready</span>
              <span data-testid="ready-count" class="text-xs font-medium text-400">{{ column.data.length - attentionCount }}</span>
            </div>

            <!-- Status group header — rendered above the first ticket of each status -->
            <div
              v-if="firstTicketIdPerStatus.get(ticket.status?.id) === ticket.id && ticketCountByStatus.get(ticket.status?.id) > 0"
              data-testid="status-header"
              class="flex justify-between items-center mb-2 mt-2 first:mt-0"
            >
              <span class="text-xs font-semibold uppercase tracking-wide text-500">{{ ticket.status?.name }}</span>
              <span data-testid="status-count" class="text-xs font-medium text-400">
                {{ ticketCountByStatus.get(ticket.status?.id) }}
              </span>
            </div>

            <div
              :class="{
                'cursor-not-allowed': ticket.pinned
              }"
              @contextmenu.prevent="$emit('context-menu', $event, ticket, column)"
            >
              <TrelloBoardItem
                :data="ticket"
                @click="$emit('click-item', ticket)"
                @delete="onDelete"
              />
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <SharedLoadingIndicator v-if="loading" :size="24" />
  </div>
</template>

<style lang="scss" scoped>
.cursor-not-allowed {
  cursor: not-allowed !important;

  .ticket-card {
    cursor: not-allowed !important;

    &:hover {
      transform: none !important;
      box-shadow: var(--shadow-1) !important;
    }
  }
}
</style>

<style lang="scss">
// Placeholder shown in the target drop position
.drag-ghost {
  opacity: 0.4;
  border: 2px dashed var(--p-primary-300) !important;
  background: var(--p-primary-50) !important;
  border-radius: 0.75rem;
}
:root[class*="app-dark"] .drag-ghost {
  border-color: var(--p-primary-700) !important;
  background: var(--p-primary-950) !important;
}

// The card being chosen/dragged — applied on mousedown
.drag-chosen {
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  outline: 2px solid var(--p-primary-400);
  outline-offset: -2px;
  z-index: 9999;
}
</style>
